import React from 'react';
import {
  Popover,
  Box,
  Button,
  IconButton,
  Typography,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * ActionPopover Component
 * Reusable popover for action forms (update status, assign user, etc.)
 * Clean, minimal design with professional styling
 */
const ActionPopover = ({
  open,
  anchorEl,
  onClose,
  onSubmit,
  title = 'Action',
  hideActions = false,
  submitDisabled = false,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit();
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: { 
          minWidth: { xs: 'calc(100vw - 32px)', sm: 380 }, 
          maxWidth: { xs: 'calc(100vw - 32px)', sm: 500 }, 
          borderRadius: { xs: 1, sm: 2 },
          boxShadow: 4,
        }
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
            {title}
          </Typography>
          <IconButton 
            size={isMobile ? 'medium' : 'small'}
            onClick={onClose}
            sx={{
              padding: { xs: '8px', sm: '4px' },
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon fontSize={isMobile ? 'medium' : 'small'} />
          </IconButton>
        </Box>
        {title && <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />}
        {onSubmit ? (
          <form onSubmit={handleSubmit}>
            {children}
            {!hideActions && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'flex-end', gap: { xs: 1, sm: 1.5 }, mt: { xs: 2, sm: 2.5 }, '& > *': { width: { xs: '100%', sm: 'auto' } } }}>
                <Button 
                  onClick={onClose} 
                  variant="outlined"
                  size={isMobile ? 'medium' : 'small'}
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size={isMobile ? 'medium' : 'small'}
                  disabled={submitDisabled}
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                >
                  Update
                </Button>
              </Box>
            )}
          </form>
        ) : (
          <Box>
            {children}
            {!hideActions && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: { xs: 2, sm: 2.5 } }}>
                <Button 
                  onClick={onClose} 
                  variant="contained" 
                  color="primary"
                  size={isMobile ? 'medium' : 'small'}
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
                >
                  Close
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default ActionPopover;
